import { getOddsScreenDateKey } from "@/lib/odds-screen/time-formatting";
import { formatDecimalOdds } from "@/utils/calculators/odds-utils";
import type {
  Board, BookId, BoardRow, BoardView, EventHeader, EventId, FamilyKind, FamilyShape, FamilySummary, Ladder, LadderBook, LadderOutcome, MarketQuote, MarketSpec, Odds, OddsDisplay, RowId,
} from "./types";

export type BoardMode = "lines" | "subject" | "results";

export function boardMode(family: FamilyShape): BoardMode {
  if (family.layout === "results") return "results";
  return family.kind === "props" || family.layout === "named" ? "subject" : "lines";
}

export function resultRows(family: Pick<FamilySummary, "markets">): "side" | "entity" {
  return family.markets[0]?.outcomes[0] === "over" ? "entity" : "side";
}

export function displayOdds(decimal: Odds, format: OddsDisplay): string {
  if (format !== "percent") return formatDecimalOdds(decimal, format);
  if (!Number.isFinite(decimal) || decimal <= 1) return "";
  return `${(100 / decimal).toFixed(1)}%`;
}

export function marketKey(raw: string): string {
  return raw.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export type OutcomeBest = {
  readonly decimal: Odds;
  readonly display: string;
  readonly books: readonly BookId[];
  readonly stakes: ReadonlyMap<BookId, number>;
  readonly held?: boolean;
};

type Candidate = { readonly book: BookId; readonly decimal: Odds; readonly stake: number | null };

export function meetsMinStake(stake: number | null | undefined, minStake: number): boolean {
  return stake === null || stake === undefined || stake >= minStake;
}

export function pricedBelowMin(market: MarketQuote, outcome: number, selected: ReadonlySet<BookId>, minStake: number): boolean {
  return market.books.some((cell) => {
    const decimal = cell.odds[outcome];
    return cell.at === "main" && selected.has(cell.book) && decimal !== null && decimal !== undefined && !meetsMinStake(cell.stakes?.[outcome], minStake);
  });
}

function byPrice(a: Candidate, b: Candidate): number {
  if (a.decimal !== b.decimal) return b.decimal - a.decimal;
  return a.book < b.book ? -1 : a.book > b.book ? 1 : 0;
}

export function bestPrices(
  market: MarketQuote,
  spec: MarketSpec,
  selected: ReadonlySet<BookId>,
  format: OddsDisplay,
  minStake: number,
): readonly (OutcomeBest | null)[] {
  return spec.outcomes.map((_, outcome) => {
    const open: Candidate[] = [];
    const held: Candidate[] = [];
    for (const cell of market.books) {
      if (cell.at !== "main" || !selected.has(cell.book)) continue;
      const decimal = cell.odds[outcome];
      const stake = cell.stakes?.[outcome] ?? null;
      if (decimal !== null && decimal !== undefined && meetsMinStake(stake, minStake)) (cell.held?.[outcome] ? held : open).push({ book: cell.book, decimal, stake });
    }
    const candidates = open.length > 0 ? open : held;
    if (candidates.length === 0) return null;
    candidates.sort(byPrice);
    const decimal = candidates[0].decimal;
    const display = displayOdds(decimal, format);
    const tied = candidates.filter((candidate) => displayOdds(candidate.decimal, format) === display);
    const books = tied.map((candidate) => candidate.book);
    const stakes = new Map(tied.flatMap((candidate) => (candidate.stake === null ? [] : [[candidate.book, candidate.stake] as const])));
    return open.length > 0 ? { decimal, display, books, stakes } : { decimal, display, books, stakes, held: true };
  });
}

export function rowHasBestPrice(row: BoardRow, specs: readonly MarketSpec[], selected: ReadonlySet<BookId>, minStake: number): boolean {
  if (selected.size === 0) return false;
  for (let slot = 0; slot < specs.length; slot += 1) {
    const market = row.markets[slot];
    if (!market) continue;
    for (let outcome = 0; outcome < specs[slot].outcomes.length; outcome += 1) {
      let top = -Infinity;
      let topSelected = false;
      for (const cell of market.books) {
        if (cell.at !== "main") continue;
        const decimal = cell.odds[outcome];
        if (decimal === null || decimal === undefined || !meetsMinStake(cell.stakes?.[outcome], minStake)) continue;
        if (decimal > top) {
          top = decimal;
          topSelected = selected.has(cell.book);
        } else if (decimal === top && selected.has(cell.book)) {
          topSelected = true;
        }
      }
      if (topSelected) return true;
    }
  }
  return false;
}

export function bestPriceRowIds(board: Board, specs: readonly MarketSpec[], selected: ReadonlySet<BookId>, minStake: number): ReadonlySet<RowId> {
  const ids = new Set<RowId>();
  for (const row of board.rows.values()) {
    if (rowHasBestPrice(row, specs, selected, minStake)) ids.add(row.id);
  }
  return ids;
}

export type Flash = "up" | "down" | null;

export function flashFor(prev: OutcomeBest | null, next: OutcomeBest | null, prevMain: number | null, nextMain: number | null): Flash {
  if (prev === null || next === null || prev.held || next.held || prevMain !== nextMain) return null;
  if (prev.decimal === next.decimal || prev.display === next.display) return null;
  return next.decimal > prev.decimal ? "up" : "down";
}

export type DayBucket = { readonly key: string; readonly count: number };

function dayKeys(view: BoardView): (eventId: EventId) => string | null {
  const keys = new Map<EventId, string | null>();
  return (eventId) => {
    let key = keys.get(eventId);
    if (key === undefined) {
      const event = view.events.get(eventId);
      key = event ? getOddsScreenDateKey(new Date(event.startMs)) : null;
      keys.set(eventId, key);
    }
    return key;
  };
}

export function dayBuckets(view: BoardView, kind: FamilyKind): readonly DayBucket[] {
  const dayOf = dayKeys(view);
  const counts = new Map<string, number>();
  const counted = new Set<EventId>();
  for (const ref of view.rows.values()) {
    const key = dayOf(ref.eventId);
    if (key === null) continue;
    if (kind === "lines") {
      if (counted.has(ref.eventId)) continue;
      counted.add(ref.eventId);
    }
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([key, count]) => ({ key, count }));
}

export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export type SearchMatcher = (...fields: readonly string[]) => boolean;
const COMPACT_QUERY_MIN = 5;
export type SearchRanker = (...fields: readonly string[]) => 0 | 1 | 2;

export function searchRanker(search: string): SearchRanker | null {
  const query = normalizeSearchText(search);
  if (query === "") return null;
  const key = query.replace(/ /g, "");
  return (...fields) => {
    const normalized = fields.map(normalizeSearchText).filter((field) => field !== "");
    if (normalized.join(" ").includes(query)) return 2;
    if (key.length < COMPACT_QUERY_MIN) return 0;
    return normalized.some((field) => field.replace(/ /g, "").includes(key)) ? 1 : 0;
  };
}

export function searchMatcher(search: string): SearchMatcher | null {
  const rank = searchRanker(search);
  return rank === null ? null : (...fields) => rank(...fields) > 0;
}

export function visibleRowIds(view: BoardView, day: string | null): readonly RowId[] {
  if (day === null) return view.rowIds;
  const dayOf = dayKeys(view);
  return view.rowIds.filter((id) => {
    const ref = view.rows.get(id);
    return ref !== undefined && dayOf(ref.eventId) === day;
  });
}

export type CollegeFilters = { readonly top25: boolean; readonly conference: string | null };

function isTop25(rank: number | undefined): boolean {
  return rank !== undefined && rank >= 1 && rank <= 25;
}

export function collegeRowIds(view: BoardView, rowIds: readonly RowId[], filters: CollegeFilters): readonly RowId[] {
  if (!filters.top25 && filters.conference === null) return rowIds;
  return rowIds.filter((id) => {
    const ref = view.rows.get(id);
    const event = ref === undefined ? undefined : view.events.get(ref.eventId);
    if (event === undefined) return false;
    if (filters.top25 && !isTop25(event.homeRank) && !isTop25(event.awayRank)) return false;
    return filters.conference === null || event.homeConf === filters.conference || event.awayConf === filters.conference;
  });
}

export function conferenceOptions(view: BoardView): readonly string[] {
  const names = new Set<string>();
  for (const event of view.events.values()) {
    if (event.homeConf !== undefined) names.add(event.homeConf);
    if (event.awayConf !== undefined) names.add(event.awayConf);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export type LadderLineSummary = { readonly line: number; readonly books: number; readonly isMain: boolean };

function pricedOutcomes(book: LadderBook): number {
  return book.outcomes.reduce((total, outcome) => (outcome === null ? total : total + 1), 0);
}

export function ladderLineSummaries(ladder: Ladder, slot: number, selected: ReadonlySet<BookId>): readonly LadderLineSummary[] {
  const summaries: LadderLineSummary[] = [];
  for (const line of ladder.lines.values()) {
    if (line.slot !== slot) continue;
    const books = line.books.filter((book) => selected.has(book.book) && pricedOutcomes(book) > 0).length;
    summaries.push({ line: line.line, books, isMain: line.line === ladder.mains[slot] });
  }
  return summaries.sort((a, b) => a.line - b.line);
}

export type OtherLine = { readonly book: BookId; readonly line: number; readonly outcomes: readonly (LadderOutcome | null)[] };

export function otherLines(ladder: Ladder, slot: number, line: number, selected: ReadonlySet<BookId>): readonly OtherLine[] {
  const present = new Set<BookId>();
  for (const candidate of ladder.lines.values()) {
    if (candidate.slot !== slot || candidate.line !== line) continue;
    for (const book of candidate.books) if (pricedOutcomes(book) > 0) present.add(book.book);
  }
  const nearest = new Map<BookId, OtherLine & { readonly priced: number }>();
  for (const candidate of ladder.lines.values()) {
    if (candidate.slot !== slot || candidate.line === line) continue;
    for (const book of candidate.books) {
      if (!selected.has(book.book) || present.has(book.book)) continue;
      const priced = pricedOutcomes(book);
      if (priced === 0) continue;
      const held = nearest.get(book.book);
      const distance = Math.abs(candidate.line - line);
      const heldDistance = held ? Math.abs(held.line - line) : Infinity;
      const better = distance < heldDistance
        || (held !== undefined && distance === heldDistance && (priced > held.priced || (priced === held.priced && candidate.line < held.line)));
      if (better) nearest.set(book.book, { book: book.book, line: candidate.line, outcomes: book.outcomes, priced });
    }
  }
  return [...nearest.values()]
    .sort((a, b) => Math.abs(a.line - line) - Math.abs(b.line - line) || (a.book < b.book ? -1 : a.book > b.book ? 1 : 0))
    .map(({ book, line: bookLine, outcomes }) => ({ book, line: bookLine, outcomes }));
}
